<template>
  <div class="slice-upload-guide">
    <!-- Loading State -->
    <div v-if="loading" class="guide-loading">
      <i class="el-icon-loading"></i>
      <span>Loading storage path…</span>
    </div>

    <!-- Content -->
    <div v-else class="guide-content">
      <!-- Steps -->
      <div class="steps">
        <div class="step">
          <div class="step-number">1</div>
          <div class="step-content">
            <div class="step-title">Organize your images</div>
            <p class="step-text">
              Place images in magnification folders (<code>40x</code>, <code>20x</code>, <code>10x</code>), 
              naming files by <strong>sampleID</strong> matching your dataset.
            </p>
            <div class="code-block">
              <pre><code>slice/
├── 40x/
│   └── raw/
│       ├── sampleID_1.jpg
│       └── sampleID_2.png
└── 10x/
    └── raw/
        └── sampleID_1.jpg</code></pre>
            </div>
          </div>
        </div>

        <div class="step">
          <div class="step-number">2</div>
          <div class="step-content">
            <div class="step-title">Upload to dataset folder</div>
            <p class="step-text">
              Copy the <code>slice/</code> folder to the server path:
            </p>
            <div class="code-block command-block">
              <code>{{ info.uuid_folder_abs || '/path/to/datasets/{uuid}' }}/</code>
              <el-button
                size="mini"
                type="text"
                icon="el-icon-document-copy"
                :disabled="!info.uuid_folder_abs"
                @click="$emit('copy', `${info.uuid_folder_abs}/`)"
              >
                Copy
              </el-button>
            </div>
          </div>
        </div>

        <div class="step">
          <div class="step-number">3</div>
          <div class="step-content">
            <div class="step-title">Generate thumbnails</div>
            
            <p class="step-label">Navigate to backend & activate environment:</p>
            <div class="code-block command-block">
              <pre><code>cd {{ backendPath }}
conda activate djangonew</code></pre>
              <el-button
                size="mini"
                type="text"
                icon="el-icon-document-copy"
                @click="$emit('copy', `cd ${backendPath} && conda activate djangonew`)"
              >
                Copy
              </el-button>
            </div>

            <p class="step-label">Run thumbnail generation:</p>
            <div class="code-block command-block">
              <code>python manage.py create_slice_thumbnails --dataset {{ datasetId }}</code>
              <el-button
                size="mini"
                type="text"
                icon="el-icon-document-copy"
                @click="$emit('copy', `python manage.py create_slice_thumbnails --dataset ${datasetId}`)"
              >
                Copy
              </el-button>
            </div>

            <p class="step-hint">
              <i class="el-icon-info"></i>
              Add <code>--overwrite</code> to regenerate existing thumbnails.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SliceUploadGuide',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    info: {
      type: Object,
      default: () => ({
        uuid: '',
        uuid_folder_abs: '',
        slice_root_abs: ''
      })
    },
    datasetId: {
      type: [String, Number],
      default: ''
    }
  },
  computed: {
    backendPath() {
      // Extract backend root from uuid_folder_abs
      // e.g., /Users/olic/Desktop/jingjie/jingjie_backend/database/datasets/uuid/
      // becomes /Users/olic/Desktop/jingjie/jingjie_backend
      const path = this.info.uuid_folder_abs || ''
      const match = path.match(/^(.+\/jingjie_backend)\/database\/datasets\//)
      return match ? match[1] : '/path/to/jingjie_backend'
    }
  }
}
</script>

<style scoped>
.slice-upload-guide {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 8px;
}

.slice-upload-guide::-webkit-scrollbar {
  width: 6px;
}

.slice-upload-guide::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.slice-upload-guide::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.slice-upload-guide::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Loading */
.guide-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 20px;
  color: #64748b;
  font-size: 14px;
}

.guide-loading i {
  font-size: 20px;
  color: #3b82f6;
}

/* Steps */
.steps {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step {
  display: flex;
  gap: 16px;
  position: relative;
}

/* Connecting line between steps */
.step:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 13px;
  top: 32px;
  width: 2px;
  height: calc(100% - 20px);
  background: linear-gradient(to bottom, #e2e8f0 0%, #f1f5f9 100%);
}

.step-number {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  position: relative;
  z-index: 1;
}

.step:hover .step-number {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #eff6ff;
}

.step-content {
  flex: 1;
  min-width: 0;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.step-text {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
  margin: 0 0 12px 0;
}

.step-text code {
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 12px;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  color: #0f172a;
}

.step-label {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  margin: 12px 0 6px 0;
}

.step-label:first-of-type {
  margin-top: 0;
}

.step-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
  margin: 12px 0 0 0;
  padding: 10px 12px;
  background: #fefce8;
  border-radius: 6px;
  border: 1px solid #fef08a;
}

.step-hint i {
  color: #ca8a04;
  margin-top: 2px;
}

.step-hint code {
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 12px;
  background: #fef9c3;
  padding: 1px 4px;
  border-radius: 3px;
}

/* Code Block */
.code-block {
  background: #1e293b;
  border-radius: 8px;
  padding: 14px 16px;
  overflow-x: auto;
}

.code-block pre {
  margin: 0;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #e2e8f0;
}

.code-block code {
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  font-size: 12px;
  color: #e2e8f0;
}

.command-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.command-block .el-button {
  color: #94a3b8;
  flex-shrink: 0;
}

.command-block .el-button:hover {
  color: #e2e8f0;
}
</style>
